import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
} from '@mui/material';
import {
  People,
  Group,
  PersonOff,
  Save,
  ArrowBack,
} from '@mui/icons-material';

interface ClientAccessProps {
  templateName: string;
  kpiCount: number;
  onSave: (finalTemplateName: string, clientVisibility: 'all' | 'selected' | 'none') => void;
  onBack: () => void;
  isLoading: boolean;
}

const ClientAccess: React.FC<ClientAccessProps> = ({
  templateName: initialTemplateName,
  kpiCount,
  onSave,
  onBack,
  isLoading,
}) => {
  const [templateName, setTemplateName] = useState(initialTemplateName);
  const [clientVisibility, setClientVisibility] = useState<'all' | 'selected' | 'none'>('all');

  const handleSave = () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }
    onSave(templateName, clientVisibility);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <People color="primary" fontSize="large" />
        <Box>
          <Typography variant="h5" gutterBottom>
            Template Name & Client Access
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Final step: Name your template and control who can access it
          </Typography>
        </Box>
      </Box>

      {/* Summary Card */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          ✅ Template Ready to Save
        </Typography>
        <Typography variant="body2">
          • {kpiCount} KPI{kpiCount !== 1 ? 's' : ''} configured<br/>
          • All questions and structure finalized<br/>
          • Ready to save and customize in the full editor
        </Typography>
      </Alert>

      {/* Template Name */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📝 Template Name
        </Typography>
        <TextField
          fullWidth
          label="Template Name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          error={!templateName.trim()}
          helperText={!templateName.trim() ? 'Template name is required' : 'You can edit this later in the template editor'}
          disabled={isLoading}
        />
      </Paper>

      {/* Client Access */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
            👥 Client Access
          </FormLabel>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Control which clients can see and use this template
          </Typography>
          <RadioGroup
            value={clientVisibility}
            onChange={(e) => setClientVisibility(e.target.value as 'all' | 'selected' | 'none')}
          >
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 1, 
                cursor: 'pointer',
                borderColor: clientVisibility === 'all' ? 'primary.main' : 'divider',
                bgcolor: clientVisibility === 'all' ? 'primary.50' : 'transparent',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => setClientVisibility('all')}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <People fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight="bold">All Clients</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Every client in your network can see and use this template
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>

            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 1, 
                cursor: 'pointer',
                borderColor: clientVisibility === 'selected' ? 'primary.main' : 'divider',
                bgcolor: clientVisibility === 'selected' ? 'primary.50' : 'transparent',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => setClientVisibility('selected')}
            >
              <FormControlLabel
                value="selected"
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Group fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight="bold">Selected Clients Only</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Only specific clients you assign can see and use this template
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>

            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                cursor: 'pointer',
                borderColor: clientVisibility === 'none' ? 'primary.main' : 'divider',
                bgcolor: clientVisibility === 'none' ? 'primary.50' : 'transparent',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => setClientVisibility('none')}
            >
              <FormControlLabel
                value="none"
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonOff fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight="bold">No Clients (Template Only)</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Not available to any clients - save as a draft template for later
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>
          </RadioGroup>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          💡 You can always change client access later from the template management panel.
        </Typography>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onBack}
          disabled={isLoading}
          startIcon={<ArrowBack />}
        >
          Back to KPIs
        </Button>
        
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={isLoading || !templateName.trim()}
          startIcon={<Save />}
          data-testid="template-final-save-button"
        >
          Save & Continue to Full Editor
        </Button>
      </Box>
    </Box>
  );
};

export default ClientAccess;

